import { type InputType, type LoanFieldProps } from 'config'
import { currentProductTypeOptionsMap } from 'hooks/useProductTypeOptions'
import { getLoanTypeMapForSelect } from 'reducers/loanDetail.reducer'
import { validateEmail } from 'utils/validator'

import { workflowRestrictions } from '../Workflows/constants'
import { ActionType } from './types'

const emailRecipientInputProps = {
  allowCustomValues: true,
  customValuePlaceholder: 'Add email address',
  customValueValidator: validateEmail,
  tooltip: 'Select Nexys users/roles or add an external email address',
}

export const includingInfoByAction = (actionType?: ActionType): Record<string, string> => {
  if (actionType == ActionType.UserAccounts)
    return {
      UserName: 'User Name',
      UserEmail: 'User Email',
      AccountType: 'Account Type',
    }
  return {
    LoanNumber: 'Loan Number',
    BorrowerName: 'Borrower Name',
    EntityName: 'Entity Name',
    SubjectPropertyAddress: 'Property Address',
    ProductType: 'Product Type',
  }
}

export const emailInputs = (): Record<string, InputType> => {
  return {
    emailTo: {
      inputType: 'multiselect',
      title: `Email to`,
      value: [],
      options: [],
      defaultSelected: false,
      required: true,
      ...emailRecipientInputProps,
    },
    type: {
      inputType: 'searchableselect',
      title: `Email Subject`,
      tooltip: 'Conditions & Template / Email Templates',
      value: '',
      options: {},
      defaultSelected: false,
      hasDefaultOption: true,
      required: true,
      sort: true,
      sortKey: 'value',
    },
    emailCc: {
      inputType: 'multiselect',
      title: `Email Cc`,
      value: [],
      options: [],
      defaultSelected: false,
      required: false,
      ...emailRecipientInputProps,
    },
    emailBcc: {
      inputType: 'multiselect',
      title: `Email Bcc`,
      value: [],
      options: [],
      defaultSelected: false,
      required: false,
      ...emailRecipientInputProps,
    },
    emailExclude: {
      inputType: 'multiselect',
      title: `Exclude`,
      value: [],
      options: [],
      defaultSelected: false,
      required: false,
      allowCustomValues: true,
      customValuePlaceholder: 'Add email address',
      customValueValidator: validateEmail,
      tooltip: 'Removed after roles expand. Use this when someone matches a recipient but should not get this email.',
    },
    content: {
      inputType: 'textarea',
      title: 'Email Content',
      rows: 5,
    },
    includes: {
      inputType: 'multiselect',
      title: `Include Information`,
      value: [],
      options: {},
      defaultSelected: false,
    },
    channels: {
      inputType: 'multiselect',
      title: 'Channel',
      options: getLoanTypeMapForSelect(),
      defaultSelected: true,
    },
    productTypes: {
      inputType: 'multiselect',
      title: 'Product Type',
      options: currentProductTypeOptionsMap(),
      defaultSelected: true,
    },
    links: {
      inputType: 'custom',
      title: '',
      render: () => {},
      span: 'full',
    },
  }
}

export const loanStatusRelationInputs = ['section1']

export const statusChangeRuleInputs = (
  loanGlobalStatus: any,
  loanFieldProperties?: LoanFieldProps,
): Record<string, InputType> => {
  return {
    statusTo: {
      inputType: 'select',
      title: 'Status Change To',
      options: loanGlobalStatus,
      hasDefaultOption: true,
      defaultOptionText: '- No Change -',
      required: false,
    },
    statusToRequireVerify: {
      inputType: 'check',
      title: 'Require Validation',
      tooltip:
        'Check all statuses of Loan (depending on the status):<br/>expiration dates, whether all conditions are cleared',
    },
    statusListBeforeToUpdate: {
      inputType: 'multiselect',
      title: `Update Loan status when below`,
      options: { ...loanGlobalStatus, noStatus: 'No Status' },
      tooltip: 'Update Loan Status when is in the status list',
      defaultSelected: false,
      required: false,
    },
    productTypes: {
      inputType: 'multiselect',
      title: 'Product Type',
      options: currentProductTypeOptionsMap(),
      defaultSelected: true,
    },
    visibleLogic: {
      inputType: 'visibleLogic',
      title: 'Use logics when below ...',
      options: {
        hasCustomOrigin: true,
        hasOtherOrigin: true,
        hasNotVisible: true,
      },
      loanFieldProperties,
      span: 'full',
    },
  }
}

export const defaultInputs = (): Record<string, InputType> => {
  return {
    section1: {
      inputType: 'section',
      title: 'Loan Status',
      span: 'full',
    },
    conditions: {
      inputType: 'multiselect',
      title: `Conditions`,
      options: workflowRestrictions,
      defaultSelected: false,
      required: false,
      visible: false,
    },
    section2: {
      inputType: 'section',
      title: 'Emails',
      span: 'full',
    },
    emailsDelayMinutes: {
      inputType: 'text',
      type: 'number',
      title: 'Email Delay (minutes)',
      tooltip:
        'Wait this many minutes after the first trigger before sending the emails in this section. Document uploads in the same window combine into one email. Leave empty or 0 to send immediately.',
      value: '',
      required: false,
    },
    section3: {
      inputType: 'section',
      title: 'Chain of Tasks',
      span: 'full',
    },
    emailDelayMinutes: {
      inputType: 'text',
      type: 'number',
      title: 'Email Delay (minutes)',
      tooltip:
        'Wait this many minutes before sending “Send email for task” notifications for tasks created by this action. Leave empty or 0 to send immediately.',
      value: '',
      required: false,
    },
    section4: {
      inputType: 'section',
      title: 'AI Prompts',
      span: 'full',
    },
  }
}

export const aiPromptInputs = (loanFieldProperties?: LoanFieldProps): Record<string, InputType> => {
  return {
    templateId: {
      inputType: 'searchableselect',
      title: 'Choose Template',
      tooltip: 'Conditions & Templates / AI Prompt Templates',
      hasDefaultOption: true,
      options: {},
      required: true,
      sort: true,
      sortKey: 'value',
    },
    projectId: {
      inputType: 'select',
      title: 'AI Project',
      hasDefaultOption: true,
      options: {},
    },
    prompt: {
      inputType: 'textarea',
      title: 'AI Prompt',
      rows: 5,
      span: 'full',
    },
    channels: {
      inputType: 'multiselect',
      title: 'Channel',
      options: getLoanTypeMapForSelect(),
      defaultSelected: true,
    },
    productTypes: {
      inputType: 'multiselect',
      title: 'Product Type',
      options: currentProductTypeOptionsMap(),
      defaultSelected: true,
    },
    noRepeat: {
      inputType: 'radio',
      title: 'Single or Always',
      options: {
        single: 'Single',
        always: 'Always',
      },
      value: 'always',
    },
    visibleLogic: {
      inputType: 'visibleLogic',
      title: 'Visible Logic',
      value: [],
      options: {
        hasCustomOrigin: true,
        hasOtherOrigin: true,
        hasNotVisible: true,
      },
      loanFieldProperties,
      span: 'full',
    },
  }
}
